import {FormikProps} from 'formik';
import {useEffect, useMemo, useRef, useState} from 'react';
import {getInputGroupByCode} from '../../../../module/dashboard/category/utilities';
import useGetSubInputLists from '../../../../services/hookService/getSubInputLists/useGetSubInputLists';
import {ShowFormError} from '../../../../utilities/helper/showFormError';
import {IProductForm} from './productFormData';

interface IProps {
  form: FormikProps<IProductForm>;
}

interface IOption {
  label: string;
  value: string;
  code: number;
}

const SelectSubInput = ({form}: IProps) => {
  const {data} = useGetSubInputLists({
    input: form?.values?.inputGuid!,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const options: IOption[] = useMemo(
    () =>
      data?.map(item => ({
        label: item.title,
        value: item.guid,
        code: item.code,
      })) || [],
    [data],
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(option =>
        option.label.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [options, search],
  );

  const selectedOption = options.find(
    option => option.value === form?.values?.subInput,
  );

  const isButtonActive = form?.values.inputGroupCode && form?.values?.inputCode;

  const inputTitle = `نوع ${
    getInputGroupByCode[form?.values?.inputGroupCode!] ?? ''
  } ${form?.values?.inputName ? ' در دسته ' + form?.values?.inputName : ''} `;

  // Close the dropdown (and reset search) when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: IOption) => {
    form?.setFieldValue('subInput', option.value);
    form?.setFieldValue('subInputCode', option.code);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <section
      ref={containerRef}
      className={`flex flex-col mt-2 relative z-[200] ${
        !isButtonActive && ' pointer-events-none opacity-40'
      }`}
    >
      <p className="mb-2 color-gray-1 text-md">
        {inputTitle} <span className="text-red-500">*</span>
      </p>

      <div
        className={`relative   ${
          form?.values?.subInput ? ' border-[1px] border-[#408d2a]' : ''
        }  rounded-lg`}
      >
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          className={`w-full flex items-center justify-between px-3 py-3.5 bg-white text-right ${
            isOpen
              ? 'border-x-[1px] border-t-[1px] border-t-neutral-400 border-x-neutral-400  rounded-t-lg'
              : 'border-[1px] border-neutral-400  rounded-lg'
          } ${form?.values?.subInput ? 'border-none' : ''}`}
        >
          <span
            className={`text-md font-normal truncate ${
              selectedOption ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            {selectedOption
              ? selectedOption.label
              : `${inputTitle} را انتخاب کنید`}
          </span>
          <i
            className={`icon-nameArrow---Up-2 text-xl text-gray-700 shrink-0 transition-transform duration-200 ${
              isOpen ? '' : 'rotate-180'
            }`}
          />
        </button>

        {isOpen && (
          <div
            className={`absolute z-50 w-full overflow-hidden bg-white  shadow-md top-full ${
              isOpen
                ? 'rounded-b-lg border-x-[1px] border-b-[1px] border-b-neutral-400 border-x-neutral-400'
                : 'rounded-lg'
            }
          `}
          >
            <div className="w-full h-[1px] bg-gray-400 "></div>
            <div className="p-2 mt-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="جستجو"
                className="w-full px-3 py-2 text-right border-[1px] border-neutral-400 rounded-md outline-none text-md placeholder:text-gray-400 focus:border-primary"
              />
            </div>

            <ul className="overflow-y-auto px-2 max-h-52">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={`w-full text-right px-3 py-2.5 text-md hover:bg-gray-50 ${
                        option.value === form?.values?.subInput
                          ? 'text-farm-shop-400 bg-gray-50'
                          : 'text-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 py-2.5 text-md text-gray-400 text-center">
                  موردی یافت نشد
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      <ShowFormError field="subInput" form={form} />
    </section>
  );
};

export default SelectSubInput;
